import { RedoClient } from "@redotech/redo-api-client";
import { contactDataFormat } from "@redotech/redo-model/customer-contact-definition";
import {
  CART_ATTRIBUTE_KEY,
  ExtendedWarrantyCartToggleExperience,
  ExtendedWarrantyPdpRenderMethod,
} from "@redotech/redo-model/extended-warranty";
import { Treatment } from "@redotech/redo-model/split";
import {
  REDO_LOADED_ON_CART_ATTRIBUTE_KEY,
  REDO_OPTED_IN_FROM_CART_ATTRIBUTE_KEY,
} from "@redotech/redo-model/storefront/cart-attributes";
import {
  AttachmentStrategy,
  isAttachmentStrategyUsingCartToggles,
  isAttachmentStrategyUsingCheckoutButtons,
} from "@redotech/redo-model/team";
import { WidgetAutocheck, WidgetFees } from "@redotech/redo-model/widget";
import {
  applyTreatment,
  pickTreatment,
} from "@redotech/redo-storefront-common/config/handle-split-testing";
import { setLocalStorageWithExpiry } from "@redotech/redo-web/utils/local-storage-wrapper";
import {
  ANONYMOUS_SHOPPER_ID_KEY,
  COVERAGE_TREATMENT_ID_QUERY_PARAM,
  CUSTOMER_EMAIL,
  CUSTOMER_PHONE,
} from "@redotech/redo-web/utils/shared-conf";
import {
  ExtensionShopifyClient,
  ShopifyAjaxClient,
  ShopifyProducts,
} from "@redotech/shopify-client/ajax";
import { storeTransform } from "@redotech/util/store";
import { sessionStore } from "@redotech/web-util/storage";
import { BigcommerceStorefrontClient } from "./client/bigcommerce-storefront";
import {
  getAutocheck,
  getFees,
  ShopifyExtensionClient,
  trackUserSession,
} from "./client/shopify-extension-client";
import { StorefrontClient } from "./client/storefront";
import { CONCIERGE_PRODUCT_FORM_CONTAINER_ID } from "./concierge/initializer";
import { log, mountRedoDebugFunction } from "./monitoring/log";
import { executeAndMonitor, initEmbedMonitor } from "./monitoring/monitor";
import { isOnProductPage, TapcartClient } from "./shopify";
import { getLocalStorageIgnoreExpiry, getStorefrontAccessToken } from "./utils";

// FIXME Fix types for globalThis
if ((globalThis as any).redo_app_loaded_on_page) {
  throw new Error("Redo already loaded");
} else {
  (globalThis as any).redo_app_loaded_on_page = true;
}

const scriptUrl = new URL(
  (document.currentScript as HTMLScriptElement).src,
  location.href,
);

const params = new URLSearchParams(new URL(scriptUrl, location.href).search);

window.redoWidgetId = window.redoWidgetId ?? params.get("widget_id");

const exchangeSelectStore = storeTransform(
  sessionStore("redo.exchange-select"),
  {
    read: (string) => string !== undefined,
    write: (value) => (value ? "" : undefined),
  },
);

let widgetAutoCheckPromise: Promise<WidgetAutocheck> | null = null;

async function getAutocheckCache(): Promise<WidgetAutocheck> {
  if (widgetAutoCheckPromise === null) {
    let foreignCurrency: string | undefined;
    let currencyExchangeRate: string | undefined;

    if (!!window.Tapcart && !window.Shopify) {
      foreignCurrency = window.Tapcart.variables.cart.currency;
      currencyExchangeRate = undefined;
    } else {
      foreignCurrency = window?.Shopify?.currency?.active;
      currencyExchangeRate = window?.Shopify?.currency?.rate;
    }

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const urlID = urlParams.get(COVERAGE_TREATMENT_ID_QUERY_PARAM);
    const onPreview =
      urlParams.get("preview") === "true" ||
      getLocalStorageIgnoreExpiry("redo_preview");
    setLocalStorageWithExpiry("redo_preview", onPreview, 0.05);
    const localStorageTreatmentId =
      getLocalStorageIgnoreExpiry("redo_treatment_id");

    const desiredTreatment = onPreview
      ? urlID || localStorageTreatmentId || undefined
      : undefined;

    const redoClient = new RedoClient(
      window.redoStorefront?.env?.REDO_API_URL ?? process.env.REDO_API_URL!,
    );
    const client = new ShopifyExtensionClient(
      redoClient,
      window.redoWidgetId,
      window.redoStorefront?.storeUrl ??
        window.Shopify?.shop ??
        window.Shopify?.Checkout?.apiHost,
    );

    const customerId: string | null =
      (window as any).ShopifyAnalytics?.meta.page.customerId || null;

    widgetAutoCheckPromise = getAutocheck(client, {
      customerId,
      foreignCurrency,
      currencyExchangeRate,
      desiredTreatment,
    });
  }

  try {
    return await widgetAutoCheckPromise;
  } catch (error) {
    widgetAutoCheckPromise = null;
    throw error;
  }
}

let widgetFeesPromise: Promise<WidgetFees> | null = null;

async function getFeesCache(): Promise<WidgetFees> {
  if (widgetFeesPromise === null) {
    const redoClient = new RedoClient(
      window.redoStorefront?.env?.REDO_API_URL ?? process.env.REDO_API_URL!,
    );
    const client = new ShopifyExtensionClient(
      redoClient,
      window.redoWidgetId,
      window.redoStorefront?.storeUrl ??
        window.Shopify?.shop ??
        window.Shopify?.Checkout?.apiHost,
    );

    widgetFeesPromise = getFees(client);
  }

  try {
    return await widgetFeesPromise;
  } catch (error) {
    widgetFeesPromise = null;
    throw error;
  }
}

void executeAndMonitor(async () => {
  if (window.blockRedo) {
    return;
  }
  const autocheck = await getAutocheckCache();

  try {
    initEmbedMonitor({ autocheck });
  } catch (error) {
    console.error("Error initializing autocheck:", error);
  }
});

void executeAndMonitor(async () => {
  if (window.blockRedo) {
    return;
  }

  const autocheck = await getAutocheckCache();

  if (autocheck?.extendedWarranties?.enabled) {
    const ajaxClient = ShopifyAjaxClient.fromEnv();
    try {
      const cart = await ajaxClient.cartGet();
      // Initialize the cart attribute if it doesn't exist
      if (!cart?.attributes?.[CART_ATTRIBUTE_KEY]) {
        await ajaxClient.updateCartAttribute({
          attributes: [{ key: CART_ATTRIBUTE_KEY, value: JSON.stringify({}) }],
        });
      }
    } catch (error) {
      log("Error initializing cart attribute", error);
    }

    const extensionShopifyClient = new StorefrontClient(
      window.Shopify?.shop ?? window.Shopify?.Checkout?.apiHost ?? "",
      getStorefrontAccessToken(autocheck) ?? "",
      window?.Shopify?.Checkout?.cartToken ||
        (await ajaxClient.cartGet()).token!,
    );

    // TODO: When these are added to ab testing, move inside the EwSettingsProvider
    if (
      autocheck?.extendedWarranties?.cartToggleExperience !==
      ExtendedWarrantyCartToggleExperience.DISABLED
    ) {
      try {
        const { initialize: initializeExtendedWarranty } = await import(
          "./extended-warranty/cart-toggle/initializer"
        );
        initializeExtendedWarranty(extensionShopifyClient);
      } catch (error: any) {
        console.error("Error initializing extended warranty", error);
      }
    }
    // TODO: When these are added to ab testing, move inside the EwSettingsProvider
    if (
      autocheck?.extendedWarranties?.pdpRenderMethod ===
      ExtendedWarrantyPdpRenderMethod.SCRIPT_TAG
    ) {
      try {
        const { initialize: initializeExtendedWarranty } = await import(
          "./extended-warranty/pdp-options/initializer"
        );
        initializeExtendedWarranty(extensionShopifyClient);
      } catch (error: any) {
        console.error("Error initializing extended warranty", error);
      }
    }
  }
});

void executeAndMonitor(async () => {
  if (window.blockRedo) {
    return;
  }
  const autocheck = await getAutocheckCache();

  if (autocheck?.showPDPDeliveryEstimate) {
    try {
      const { initialize: initializeDeliveryEstimate } = await import(
        "./delivery-estimate"
      );
      initializeDeliveryEstimate(autocheck);
    } catch (error: any) {
      console.error("Error initializing delivery estimate:", error);
    }
  }
});

void executeAndMonitor(async () => {
  if (window.blockRedo) {
    return;
  }
  const autocheck = await getAutocheckCache();

  try {
    const { initialize: initializeMarketingEmbeddedForm } = await import(
      "./marketing-embedded-form/marketing-embedded-form"
    );
    initializeMarketingEmbeddedForm(autocheck);
  } catch (error: any) {
    console.error("Error initializing marketing embedded form:", error);
  }
});

void executeAndMonitor(async () => {
  if (window.blockRedo) {
    return;
  }
  const autocheck = await getAutocheckCache();

  try {
    const { initialize: initializeReviewStars } = await import(
      "./reviews/review-stars"
    );
    initializeReviewStars(autocheck);
  } catch (error: any) {
    console.error("Error initializing review stars:", error);
  }
});

void executeAndMonitor(async () => {
  if (window.blockRedo) {
    return;
  }
  const onTapcart = !!window.Tapcart && !window.Shopify;
  const autocheck = await getAutocheckCache();

  try {
    if (
      autocheck.marketing.enabled &&
      (document.querySelector(CONCIERGE_PRODUCT_FORM_CONTAINER_ID) ||
        isOnProductPage(onTapcart))
    ) {
      const { addBackInStockPdpButton } = await import("./product-page");
      addBackInStockPdpButton(autocheck, onTapcart);
      log("Added back in stock PDP button");

      const { initialize: initializeBackInStockPdpButton } = await import(
        "./product-back-in-stock/initializer"
      );
      initializeBackInStockPdpButton(autocheck);
    }
  } catch (error: any) {
    console.error("Error initializing back in stock PDP button:", error);
  }
});

void executeAndMonitor(async () => {
  if (window.blockRedo) {
    return;
  }

  const autocheck = await getAutocheckCache();

  try {
    const { initialize } = await import("./redo-storefront/index");
    initialize(autocheck);
  } catch (error: any) {
    console.error("Error initializing redo storefront", error);
    (globalThis as any).redo_app_loaded_on_page = false;
  }
});

void executeAndMonitor(async () => {
  if (window.blockRedo) {
    return;
  }

  const onTapcart = !!window.Tapcart && !window.Shopify;

  const redoClient = new RedoClient(
    window.redoStorefront?.env?.REDO_API_URL ?? process.env.REDO_API_URL!,
  );
  const widgetClient = new ShopifyExtensionClient(
    redoClient,
    window.redoWidgetId,
    window.redoStorefront?.storeUrl ??
      window.Shopify?.shop ??
      window.Shopify?.Checkout?.apiHost,
  );

  const autocheck = await getAutocheckCache();

  if (autocheck?.customerWidgetEnabled) {
    log("Adding customer widget script");
    const widgetId = window.redoWidgetId;
    const storeUrl = window.Shopify?.shop ?? window.Shopify?.Checkout?.apiHost;
    if (!widgetId && !storeUrl) {
      console.error("Widget ID or storeUrl required");
      return;
    }

    const CHAT_WIDGET_BASE =
      process.env.REDO_CHAT_WIDGET_URL || "https://chat-widget.getredo.com";

    const script = document.createElement("script");
    script.src = widgetId
      ? `${CHAT_WIDGET_BASE}/widget.js?widgetId=${widgetId}`
      : `${CHAT_WIDGET_BASE}/widget.js?storeUrl=${storeUrl}`;
    script.async = true;
    document.head.appendChild(script);

    if (
      autocheck.concierge.placeConciergeProductForm &&
      (document.querySelector(CONCIERGE_PRODUCT_FORM_CONTAINER_ID) ||
        isOnProductPage(onTapcart))
    ) {
      const { addConciergeProductForm } = await import("./product-page");
      addConciergeProductForm(autocheck, onTapcart);

      const { initialize } = await import("./concierge/initializer");
      initialize({
        fromThemeExtension: true,
        autocheckSettings: autocheck.concierge.conciergeProductFormSettings,
      });
    }

    // concierge enabled when support.settings.ai.enabled is true
    if (autocheck.customerAccounts.enabled || autocheck.concierge.enabled) {
      try {
        await trackUserSession(widgetClient);
      } catch (error: any) {
        console.error("Error sending user session", error);
      }
    }
  }
});

void executeAndMonitor(async () => {
  if (exchangeSelectStore.get()) {
    const { initialize } = await import("./exchange-select");
    initialize();
    return;
  }

  if (window.blockRedo) {
    return;
  }
  const onTapcart = !!window.Tapcart && !window.Shopify;

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const urlID = urlParams.get(COVERAGE_TREATMENT_ID_QUERY_PARAM);
  const onPreview =
    urlParams.get("preview") === "true" ||
    getLocalStorageIgnoreExpiry("redo_preview");
  setLocalStorageWithExpiry("redo_preview", onPreview, 0.05);
  const localStorageTreatmentId =
    getLocalStorageIgnoreExpiry("redo_treatment_id");

  const autocheck = await getAutocheckCache();

  let selectedTreatment: Treatment | undefined;
  if (autocheck.treatments) {
    selectedTreatment = pickTreatment(
      autocheck.treatments,
      localStorageTreatmentId,
      urlID,
    );
  }
  const fees = await getFeesCache();
  if ("error" in fees) {
    console.error("error in fees endpoint", { error: fees.error });
    return;
  }

  let extensionShopifyClient: ExtensionShopifyClient;

  if (window.redoStorefront?.type === "bigcommerce") {
    extensionShopifyClient = new BigcommerceStorefrontClient({
      storeUrl: window.redoStorefront.storeUrl,
      auth: getStorefrontAccessToken(autocheck),
    });
  } else if (onTapcart) {
    extensionShopifyClient = new TapcartClient(
      getStorefrontAccessToken(autocheck),
      (window as any)["STORE_URL"],
      window.Tapcart,
    );
  } else if (window.Shopify?.Checkout?.apiHost == "denim-tears.myshopify.com") {
    extensionShopifyClient = new StorefrontClient(
      "denim-tears.myshopify.com",
      getStorefrontAccessToken(autocheck),
      window?.Shopify?.Checkout?.cartToken ||
        (await ShopifyAjaxClient.fromEnv().cartGet()).token!,
    );
  } else if (window.Shopify?.Checkout?.apiHost == "ensorings.myshopify.com") {
    extensionShopifyClient = new StorefrontClient(
      "ensorings.myshopify.com",
      getStorefrontAccessToken(autocheck),
      window?.Shopify?.Checkout?.cartToken ||
        (await ShopifyAjaxClient.fromEnv().cartGet()).token!,
    );
  } else {
    extensionShopifyClient = ShopifyAjaxClient.fromEnv();
  }

  // Set up debug logging for the shopify and checkout extensions
  mountRedoDebugFunction(extensionShopifyClient);

  let cart = await extensionShopifyClient.cartGet();

  const cartAttrUpdates: { key: string; value: string }[] = [];

  if (selectedTreatment) {
    const treatmentId = applyTreatment({
      treatment: selectedTreatment,
      autocheck,
      fees,
    });
    setLocalStorageWithExpiry("redo_treatment_id", treatmentId, 0.5);
    if (cart.attributes?.redo_treatment_id !== treatmentId) {
      cartAttrUpdates.push({ key: "redo_treatment_id", value: treatmentId });
    }
  } else {
    if (cart.attributes?.redo_treatment_id) {
      cartAttrUpdates.push({ key: "redo_treatment_id", value: "" });
    }
  }

  const shouldMarkRedoLoadedOnCart =
    autocheck.usingCartAndCheckoutToggle ||
    isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy];

  if (shouldMarkRedoLoadedOnCart && !cart.attributes?.__redo_loaded_on_cart) {
    cartAttrUpdates.push({
      key: REDO_LOADED_ON_CART_ATTRIBUTE_KEY,
      value: "true",
    });
  }

  if (
    cart.attributes?.redo_opted_in_from_cart &&
    !autocheck.setCheckoutToggleBasedOnOptInCartAttribute
  ) {
    cartAttrUpdates.push({
      key: REDO_OPTED_IN_FROM_CART_ATTRIBUTE_KEY,
      value: "",
    });
  }

  if (cartAttrUpdates.length) {
    const updatedCart = await extensionShopifyClient.updateCartAttribute({
      attributes: cartAttrUpdates,
    });
    if (updatedCart) {
      cart = updatedCart;
    }
  }

  const cartToken = window?.Shopify?.Checkout?.cartToken || cart.token;
  const shop = window.Shopify?.shop ?? window.Shopify?.Checkout?.apiHost;
  let storefrontClient: StorefrontClient | undefined = undefined;
  let storefrontTestPromise;
  if (
    !onTapcart &&
    cartToken &&
    shop &&
    getStorefrontAccessToken(autocheck) &&
    cartToken.includes("?")
  ) {
    storefrontClient = new StorefrontClient(
      shop,
      getStorefrontAccessToken(autocheck),
      cartToken,
    );
    storefrontTestPromise = storefrontClient.cartGet();
  }

  if (onTapcart) {
    // In the tapcart integration the cart is the best place to show our toggle so we override that setting
    autocheck.attachmentStrategy = AttachmentStrategy.SINGLE_TOGGLE;
  }

  if (
    autocheck.disableRedoForCustomer ||
    !autocheck.enabled ||
    !fees ||
    !fees.handle ||
    (!autocheck.coverageEnabled &&
      !autocheck.packageProtection?.coverageEnabled)
  ) {
    return;
  }

  let shopifyProducts: ShopifyProducts = {};
  if (autocheck.paidModel !== "merchant_paid") {
    if (
      autocheck.packageProtection.splitProducts &&
      fees.returnProduct?.handle &&
      fees.packageProduct?.handle &&
      fees.bothProduct?.handle
    ) {
      const promises = [
        extensionShopifyClient.productGetJS(fees.returnProduct.handle),
      ];
      promises.push(
        extensionShopifyClient.productGetJS(fees.packageProduct.handle),
      );
      promises.push(
        extensionShopifyClient.productGetJS(fees.bothProduct.handle),
      );

      const products = await Promise.all(promises);
      for (const product of products) {
        product.variants.sort((a, b) => a.price - b.price);
      }

      const mainShopifyProduct = products[2];
      const returnShopifyProduct = products[0];
      const packageShopifyProduct = products[1];
      const bothShopifyProduct = products[2];

      shopifyProducts = {
        mainShopifyProduct,
        returnShopifyProduct,
        packageShopifyProduct,
        bothShopifyProduct,
      };
    } else {
      const product = await extensionShopifyClient.productGetJS(fees.handle);
      product.variants.sort((a, b) => a.price - b.price);
      shopifyProducts = { mainShopifyProduct: product };
    }
  }

  let canUseStorefrontAPI = false;
  if (storefrontClient && storefrontTestPromise) {
    try {
      canUseStorefrontAPI = !!(await storefrontTestPromise);
    } catch {
      log("storefront client failed");
    }
  }

  log(`Can use storefront api? ${canUseStorefrontAPI}`);

  const cartEnabled =
    autocheck.cartToggle?.enabled ||
    isAttachmentStrategyUsingCartToggles[autocheck.attachmentStrategy] ||
    location.search.includes("redo-cart=true") ||
    sessionStorage.getItem("redo-cart") === "true" ||
    onTapcart; // Tapcart always has cart enabled

  const { initialize } = await import("./manager");
  initialize(
    extensionShopifyClient,
    canUseStorefrontAPI ? storefrontClient : undefined,
    autocheck,
    cartEnabled,
    fees,
    shopifyProducts,
  );
});

async function ensureMarketingAnonymousIdPresent() {
  const autocheck = await getAutocheckCache();

  if (autocheck.marketing.enabled) {
    const anonymousShopperId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);
    if (!anonymousShopperId) {
      localStorage.setItem(ANONYMOUS_SHOPPER_ID_KEY, crypto.randomUUID());
    }
  }
}
void executeAndMonitor(async () =>
  ensureMarketingAnonymousIdPresent().catch(console.error),
);

function getCustomerEmailAndPhone() {
  const queryParameters = new URLSearchParams(window.location.search);
  const contactData = queryParameters.get("utm_contact");
  if (!contactData) return;

  const { email, phone } = contactDataFormat.read(decodeURI(contactData));

  if (email) {
    localStorage.setItem(CUSTOMER_EMAIL, email);
  }
  if (phone) {
    localStorage.setItem(CUSTOMER_PHONE, phone);
  }
}
void executeAndMonitor(async () => getCustomerEmailAndPhone());
